// Shared admin bottom-sheet chrome: scrim-tap and Escape both close, and Escape only
// ever closes the TOPMOST sheet (v1's stacking rule — Job Detail closes back to the
// Job Database, not everything at once).
import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';

// Open sheets, keyed by their DOM node. "Topmost" is decided by DOCUMENT ORDER at
// keypress time, not by mount order — engines disagree about effect ordering when a
// keyed sheet mounts beside unkeyed ones (WebKit inverts it), but DOM order always
// matches the JSX stacking order in App.
const sheets = new Map<HTMLElement, () => void>();
let wired = false;
function wireEscape(): void {
  if (wired) return;
  wired = true;
  // Capture phase: WebKit's native search-input Escape handling (clear + swallow)
  // otherwise eats the key before it bubbles back to window.
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape' || !sheets.size) return;
      let top: HTMLElement | null = null;
      for (const el of sheets.keys()) {
        if (!el.isConnected) continue;
        if (!top || top.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) top = el;
      }
      if (!top) return;
      e.preventDefault();
      sheets.get(top)!();
    },
    true,
  );
}

interface Props {
  title: string;
  sub?: string;
  onClose: () => void;
  children: ComponentChildren;
  /** Full-height variant for list-heavy surfaces (Job Database, History). */
  tall?: boolean;
  testId?: string;
}

export function AdminSheet({ title, sub, onClose, children, tall, testId }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  // Layout effect, not useEffect: registration must be synchronous with DOM insertion.
  // Preact defers useEffect past paint, leaving a window where the sheet is visible
  // but unregistered — an Escape in that window would close the sheet BELOW it.
  useLayoutEffect(() => {
    wireEscape();
    const el = ref.current;
    if (el) sheets.set(el, onClose);
    // Move focus INTO the dialog (a11y, and WebKit never focuses buttons on click —
    // without this, Escape keeps landing in whatever input opened the sheet).
    el?.focus();
    return () => {
      if (el) sheets.delete(el);
    };
    // mount-only on purpose: the element identity is the registration key.
  }, []);

  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={ref}
        tabindex={-1}
        class={`sheet flex flex-col outline-none ${tall ? 'h-[92dvh]' : 'max-h-[92dvh]'}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
      >
        <div class="flex items-start gap-2 pb-2">
          <div class="min-w-0">
            <h2 class="text-lg font-extrabold text-navy-900">{title}</h2>
            {sub && <p class="text-sm text-slate-500">{sub}</p>}
          </div>
          <button
            class="tap ml-auto shrink-0 rounded-lg px-2 text-2xl leading-none text-slate-400"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
      </div>
    </div>
  );
}
